import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CalendarIcon, ClockIcon, MapPinIcon, UserIcon } from '@heroicons/react/24/outline';
import aedIcon from '../assets/aed.svg';
import { trackFormSubmission, trackUserInteraction } from '../utils/analytics';
import { saveBooking } from '../utils/bookingService';
import { useNavigate } from 'react-router-dom';
import { getDiscountedPrice } from '../config/pricing';

const Booking: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventType: '',
    package: '',
    date: '',
    time: '',
    location: '',
    details: ''
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [submissionMessage, setSubmissionMessage] = useState<string | null>(null);

  const eventTypes = [
    'Wedding',
    'Portrait Session',
    'Corporate Event',
    'Family Session',
    'Engagement',
    'Other'
  ];

  const packages = [
    { id: 'basic-photo', name: 'Basic Photo Package – 1 Hour', price: getDiscountedPrice('Basic Photo Package – 1 Hour') },
    { id: 'basic-video', name: 'Basic Video Package – 1 Hour', price: getDiscountedPrice('Basic Video Package – 1 Hour') },
    { id: 'combo', name: 'Combo Package (Photo + Video)', price: getDiscountedPrice('Combo Package (Photo + Video)') },
    { id: 'custom', name: 'Custom Package', price: 'Quote' }
  ];

  const timeSlots = [
    '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
    '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM',
    '5:00 PM', '6:00 PM', '7:00 PM', '8:00 PM'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    else {
      const digitsOnly = formData.phone.replace(/\D+/g, '');
      if (digitsOnly.length < 10) newErrors.phone = 'Phone must have at least 10 digits';
    }
    if (!formData.eventType) newErrors.eventType = 'Event type is required';
    if (!formData.package) newErrors.package = 'Package selection is required';
    if (!formData.date) newErrors.date = 'Date is required';
    if (!formData.time) newErrors.time = 'Time is required';
    else {
      const time12h = /^\d{1,2}:\d{2}\s?(AM|PM)$/i.test(formData.time);
      const time24h = /^\d{2}:\d{2}(:\d{2})?$/.test(formData.time);
      if (!time12h && !time24h) newErrors.time = 'Use 13:00:00 or 1:00 PM';
    }
    if (!formData.location.trim()) newErrors.location = 'Location is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        trackUserInteraction('form_start', 'booking_form');
        
        // Save booking data to backend and get booking ID
        console.log('Saving booking data:', formData);
        const bookingId = await saveBooking(formData);
        console.log('Booking saved with ID:', bookingId);
        trackFormSubmission('booking', true);
        // Show professional confirmation message instead of redirecting
        setSubmissionSuccess(true);
        setSubmissionMessage(
          "Thank you for your request. We've received your booking and started processing it. " +
          "A confirmation will be sent to your email, and our specialist will contact you within the next hour."
        );
      } catch (error) {
        console.error('Error processing booking:', error);
        // Surface backend validation errors if present
        const message = error instanceof Error ? error.message : 'Unknown error';
        setErrors(prev => ({ ...prev, submit: message }));
        trackFormSubmission('booking', false, message);
        // Optionally keep alert for critical failures
        // alert(message);
      }
    } else {
      trackFormSubmission('booking', false, 'Validation failed');
    }
  };

  return (
    <section id="booking" className="py-20 bg-white logo-watermark logo-watermark-light">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Book Your Session
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Fill out the form below and we'll get back to you within 24 hours to confirm your booking
          </p>
        </motion.div>

        {/* Booking Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto"
        >
          {submissionSuccess ? (
            <div className="bg-white rounded-2xl p-8 shadow-lg text-center border border-green-200">
              <h3 className="text-2xl font-bold text-green-700 mb-3">Request received</h3>
              <p className="text-gray-700">
                {submissionMessage || "Thank you for your request. We've received your booking and started processing it. A confirmation will be sent to your email, and our specialist will contact you within the next hour."}
              </p>
            </div>
          ) : (
          <form onSubmit={handleSubmit} className="bg-gray-50 rounded-2xl p-6 sm:p-8 shadow-lg">
            {/* Personal Information */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <UserIcon className="h-6 w-6 mr-2" />
                Personal Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-black focus:border-transparent ${
                      errors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter your full name"
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-black focus:border-transparent ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter your email"
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-black focus:border-transparent ${
                      errors.phone ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter your phone number"
                  />
                  {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                </div>
              </div>
            </div>

            {/* Event Details */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <CalendarIcon className="h-6 w-6 mr-2" />
                Event Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Event Type *
                  </label>
                  <select
                    name="eventType"
                    value={formData.eventType}
                    onChange={handleInputChange}
                    aria-label="Event Type"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-black focus:border-transparent ${
                      errors.eventType ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select event type</option>
                    {eventTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  {errors.eventType && <p className="text-red-500 text-sm mt-1">{errors.eventType}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    Photography Package *
                    <img src={aedIcon} alt="AED" className="w-4 h-4 ml-2" />
                  </label>
                  <select
                    name="package"
                    value={formData.package}
                    onChange={handleInputChange}
                    aria-label="Photography Package"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-black focus:border-transparent ${
                      errors.package ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select a package</option>
                    {packages.map(pkg => (
                      <option key={pkg.id} value={pkg.id}>
                        {pkg.name} - {pkg.price === 'Quote' ? pkg.price : `${pkg.price}`}
                      </option>
                    ))}
                  </select>
                  {errors.package && <p className="text-red-500 text-sm mt-1">{errors.package}</p>}
                </div>
              </div>
            </div>

            {/* Date & Time */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <ClockIcon className="h-6 w-6 mr-2" />
                Date & Time
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Date *
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    min={new Date().toISOString().split('T')[0]}
                    title="Preferred Date"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-black focus:border-transparent ${
                      errors.date ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.date && <p className="text-red-500 text-sm mt-1">{errors.date}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Time *
                  </label>
                  <select
                    name="time"
                    value={formData.time}
                    onChange={handleInputChange}
                    aria-label="Preferred Time"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-black focus:border-transparent ${
                      errors.time ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select time</option>
                    {timeSlots.map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                  {errors.time && <p className="text-red-500 text-sm mt-1">{errors.time}</p>}
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <MapPinIcon className="h-6 w-6 mr-2" />
                Location
              </h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Location *
                </label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-black focus:border-transparent ${
                    errors.location ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter the event location or venue"
                />
                {errors.location && <p className="text-red-500 text-sm mt-1">{errors.location}</p>}
              </div>
            </div>

            {/* Additional Details */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Details
              </label>
                  <textarea
                    name="details"
                    value={formData.details}
                    onChange={handleInputChange}
                    rows={4}
                    aria-label="Additional Details"
                    title="Additional Details"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                    placeholder="Tell us more about your event, special requirements, or any questions you have..."
                  />
            </div>

            {/* Submit Button */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              className="w-full bg-black text-white py-4 px-8 rounded-lg font-semibold text-lg hover:bg-gray-800 transition-colors duration-200"
            >
              Submit Booking Request
            </motion.button>
          </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default Booking;
